import React from 'react';
import Button from '../button/button';
import styles from './table-page-controls.module.scss';
import classNames from 'classnames';
import * as Select from '@radix-ui/react-select';

const defaultPageSizeOptions = {
  'ten': 10,
  'fifty': 50,
  'all': -1,
} as const;

interface TablePageControlsProps {
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  onPageSizeChange?: (size: number) => void; // size -1 for "All"
  // Rótulo -> tamanho. -1 vira "All" na tela. Default cobre os usos existentes (maintenance/*);
  // report-jobs passa o próprio conjunto, sem "All" -- paginação lá é do servidor, com teto.
  pageSizeOptions?: Record<string, number>;
  setPageIndex(pageIndex: number): void;
  getCanPreviousPage(): boolean;
  getCanNextPage(): boolean;
  previousPage(): void;
  nextPage(): void;
}

const TablePageControls = ({
  pageCount,
  pageIndex,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = defaultPageSizeOptions,
  setPageIndex,
  getCanPreviousPage,
  getCanNextPage,
  previousPage,
  nextPage,
}: TablePageControlsProps) => {
  const getVisiblePageNumbers = () => {
    const currentPage = pageIndex + 1;
    const totalPages = pageCount;
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max
      return Array.from({length: totalPages}, (_, i) => i + 1);
    }

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handlePageSizeChange = (value: string) => {
    const newPageSize = pageSizeOptions[value];

    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  return (
    <div className={styles.paginationContainer}>
      <Select.Root onValueChange={handlePageSizeChange}>
        <Select.Trigger className={styles.selectTrigger}>
          <Select.Value
            className={classNames(styles.paginationText, styles.selectTriggerText)}
            placeholder={pageSize === -1 ? 'All' : pageSize}
          />
          <Select.Icon>
            <i className='fas fa-caret-down fa-lg' />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            className={styles.selectContent}
            position='popper'
            side='bottom'
          >
            <Select.ScrollUpButton />
            <Select.Viewport>
              {Object.entries(pageSizeOptions).map(([key, size]) => (
                <Select.Item
                  key={key}
                  value={key}
                  className={styles.selectItem}
                >
                  <Select.ItemText>
                    {size === -1 ? 'All' : size}
                  </Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
            <Select.ScrollDownButton />
          </Select.Content>
        </Select.Portal>
      </Select.Root>

      <div className={styles.paginationButtonsContainer}>
        {/* First button - only show if there are multiple pages and we're not on page 1 */}
        {pageCount > 5 && pageIndex > 2 && (
          <Button
            size='small'
            className={classNames(styles.paginationButton, styles.paginationButtonSecondary)}
            onClick={() => setPageIndex(0)}
          >
            <span className={classNames(styles.paginationText, styles.paginationButtonText)}>
              First
            </span>
          </Button>
        )}

        {/* <Button size='small' className={styles.paginationButton}>
          <span className={classNames(styles.paginationText, styles.paginationButtonText)}>
            {'1'.padStart(2, '0')}
          </span>
        </Button> */}

        {/* Previous button - only show if not on first page */}
        {getCanPreviousPage() && (
          <Button
            size='small'
            className={classNames(styles.paginationButton, styles.paginationButtonSecondary)}
            onClick={() => previousPage()}
          >
            <span className={classNames(styles.paginationText, styles.paginationButtonText)}>
              Prev
            </span>
          </Button>
        )}

        {getVisiblePageNumbers().map((pageNumber) => (
          <Button key={pageNumber} size='small' className={styles.paginationButton} data-selected={pageIndex === pageNumber-1} onClick={() => {
            setPageIndex(pageNumber - 1); // pageNumber is 1-based, table uses 0-based index
          }}>
            <span className={classNames(styles.paginationText, styles.paginationButtonText)}>
              {pageNumber.toString().padStart(2, '0')}
            </span>
          </Button>
        ))}

        {/* Next button - only show if not on last page */}
        {getCanNextPage() && (
          <Button
            size='small'
            className={classNames(styles.paginationButton, styles.paginationButtonSecondary)}
            onClick={() => nextPage()}
          >
            <span className={classNames(styles.paginationText, styles.paginationButtonText)}>
              Next
            </span>
          </Button>
        )}

        {/* Last button - only show if there are many pages and we're not near the end */}
        {pageCount > 5 && pageIndex < pageCount - 3 && (
          <Button
            size='small'
            className={classNames(styles.paginationButton, styles.paginationButtonSecondary)}
            onClick={() => setPageIndex(pageCount - 1)}
          >
            <span className={classNames(styles.paginationText, styles.paginationButtonText)}>
              Last
            </span>
          </Button>
        )}
      </div>
    </div>
  );
};
TablePageControls.displayName = 'TablePageControls';

export default TablePageControls;
